import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Select, Tooltip, Upload } from "antd";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomFormItem } from "../styled";
import DateRangePicker from "../DateRangePicker";
import {
	ALLOW_EXTENSION_IMAGE_FILE,
	MESSAGE_VALIDATION,
	validateStampImages,
} from "common/common";
import { TapAnimationComponent } from "components";
import { stampService } from "services";
import { API, COMMONS } from "common";
import { useAdminLayoutContext } from "layouts/AdminMain/AdminMainContext";
import { StyleScrollBarTextArea } from "views/Admin/Stamps/CreateNewStamp/styled";

const MAX_TOTAL_STAMP = 5;

const CreateNewStamp = (props) => {
	const [form] = Form.useForm();
	const [maxMidtermGoal, setMaxMidtermGoal] = useState();
	const [fileList, setFileList] = useState([]);
	const queryClient = useQueryClient();
	const { isBlock } = useAdminLayoutContext();

	const optionsTotalStamp = Array.from({ length: MAX_TOTAL_STAMP }, (_, i) => ({
		label: (i + 1).toString(),
		value: i + 1,
	}));

	const midtermGoalOptions = useMemo(() => {
		if (!maxMidtermGoal || maxMidtermGoal === 1) return [];

		return Array.from({ length: maxMidtermGoal - 1 }, (_, i) => ({
			label: (i + 1).toString(),
			value: i + 1,
		}));
	}, [maxMidtermGoal]);

	const handleBeforeUploadFile = async (file) => {
		const validateStampImageMessage = await validateStampImages(file);

		if (validateStampImageMessage) {
			message.error(validateStampImageMessage);
			return false;
		}

		return false;
	};

	const handleChangeUploadFile = async (param, index) => {
		const file = param?.file;

		const isInvalidStampImage = await validateStampImages(file);

		if (!isInvalidStampImage) {
			const newList = [...fileList];
			newList[index] = {
				uid: file?.uid,
				name: file?.name,
				status: "done",
				originFileObj: file,
				url: URL.createObjectURL(file),
			};
			setFileList(newList);
		}
	};

	const { mutateAsync: createNewStamp, isLoading: isCreatingStamp } =
		useMutation({
			mutationFn: async (data) => {
				try {
					await stampService.create(data);

					message.success(COMMONS.SUCCESS_ADD_MSG);
					queryClient.invalidateQueries([API.QUERY_KEY_ADMIN_STAMPS]);
				} catch (error) {
					console.log("error :>> ", error);
				}
			},
		});

	const handleCreateNewStamp = async () => {
		try {
			if (maxMidtermGoal && fileList.length < maxMidtermGoal) {
				message.error(MESSAGE_VALIDATION.PLEASE_UPLOAD_FULL_PHOTOS);
				return;
			}

			const data = await form.validateFields();
			const formData = new FormData();
			const fields = [
				"name",
				"description",
				"fromDate",
				"toDate",
				"totalStamp",
				"midtermGoal",
				"ultimateGoalMess",
				"midtermGoalMess",
				"note",
			];

			fields.forEach((field) => {
				if (data[field]) {
					formData.append(field, String(data[field])?.trim());
				}
			});
			fileList?.forEach((item) =>
				formData.append("stamp_images", item?.originFileObj || item?.file),
			);

			createNewStamp(formData);
		} catch (error) {
			console.log(error);
		}
	};

	const urlToFile = async (url, filename) => {
		const res = await fetch(url);
		const blob = await res.blob();
		return new File([blob], filename, { type: blob.type });
	};

	const handleGenerateDefaultFiles = async (goal) => {
		try {
			const files = await Promise.all(
				Array.from({ length: goal }).map((_, index) =>
					urlToFile(
						`/default-stamp-images/stamp-${index + 1}.png`,
						`stamp-${index + 1}.png`,
					),
				),
			);

			const filesWithUrl = files.map((file) => ({
				file,
				url: URL.createObjectURL(file),
			}));

			setFileList(filesWithUrl);
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<StyleScrollBarTextArea>
			<div className="flex justify-between items-center mb-4 py-[23px] px-[29px] border-b border-[#cfcfcf]">
				<div className="text-[18px] font-bold">スタンプラリー</div>
				<div>
					<TapAnimationComponent className="inline-block">
						<Button
							type="primary"
							size="large"
							className="w-32"
							onClick={form.submit}
							loading={isCreatingStamp}
							disabled={isBlock(COMMONS.ACTION_KEY.CREATE_STAMP)}
						>
							保存
						</Button>
					</TapAnimationComponent>
				</div>
			</div>
			<div className="flex flex-col gap-y-6 px-[29px]">
				<CustomFormItem>
					<Form
						size="large"
						requiredMark={false}
						form={form}
						onFinish={handleCreateNewStamp}
						colon={false}
						disabled={isBlock(COMMONS.ACTION_KEY.CREATE_STAMP)}
					>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<span className="font-bold">スタンプラリー名</span>
									<Tooltip
										title={<div>最大30文字です。</div>}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							rules={[
								{
									required: true,
									whitespace: true,
									message: "スタンプラリー名は必須項目です",
								},
								{
									max: 30,
									message: "スタンプラリー名は30文字以内にしてください",
								},
							]}
							name="name"
							className="min-w-[300px] max-w-[800px]"
						>
							<Input allowClear />
						</Form.Item>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<span className="font-bold">説明テキスト</span>
									<Tooltip
										title={<div>最大100文字です。</div>}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							name="description"
							rules={[
								{
									max: 100,
									message: "説明テキストは100文字以内にしてください",
								},
							]}
						>
							<Input.TextArea
								allowClear
								style={{ resize: "none" }}
								className="min-w-[300px] max-w-[800px]"
							/>
						</Form.Item>
						<div className="border-b border-[#cfcfcf] my-4"></div>
						<DateRangePicker
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<span className="font-bold">イベント期間</span>
								</p>
							}
							rules={[
								{
									required: true,
									whitespace: true,
									message: "イベント期間は必須項目です",
								},
							]}
							form={form}
						/>
						<div className="border-b border-[#cfcfcf] my-4"></div>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<span className="font-bold">スタンプラリーの数</span>
									<Tooltip
										title={
											<div className="whitespace-pre-line">
												設定後にスタンプラリーの数を変更した場合、スタンプ画像の設定はリセットされます。
											</div>
										}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							name="totalStamp"
							rules={[
								{
									required: true,
									message: "スタンプラリーの数は必須項目です",
								},
							]}
							className="w-[355px]"
						>
							<Select
								allowClear
								options={optionsTotalStamp}
								onChange={(value) => {
									setMaxMidtermGoal(value);
									handleGenerateDefaultFiles(value);
									form.setFieldValue("midtermGoal", undefined);
									if (value <= 1) {
										form.resetFields(["midtermGoalMess"]);
									}
								}}
							/>
						</Form.Item>
						<div className="border-b border-[#cfcfcf] my-4"></div>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<span className="font-bold">スタンプ画像</span>
									<Tooltip
										title={
											<div className="whitespace-pre-line">
												{`
												※100KB以内で500×500pxの画像をアップロードしてください。\n
												※ .jpg, .jpeg, .png, .JPG, .JPEG, .PNGの画像をアップロード可能です。\n
												※スタンプ画像を変更したい場合は、画像を上書きで登録ください。
												`}
											</div>
										}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							rules={[{ required: true, whitespace: true }]}
						>
							{maxMidtermGoal && (
								<div className="flex flex-wrap gap-4">
									{Array.from({ length: maxMidtermGoal }).map((_, index) => {
										const file = fileList[index];

										return (
											<div key={index} className="relative flex items-center">
												<div>{index + 1}: &nbsp; </div>
												<Upload
													accept={ALLOW_EXTENSION_IMAGE_FILE}
													showUploadList={false}
													beforeUpload={handleBeforeUploadFile}
													onChange={(param) =>
														handleChangeUploadFile(param, index)
													}
												>
													<div className="cursor-pointer">
														<img
															src={file?.url}
															width={78}
															height={78}
															preview
															className="rounded-full object-cover bg-[#eee]"
															alt=""
														/>
													</div>
												</Upload>
											</div>
										);
									})}
								</div>
							)}
						</Form.Item>
						<div className="border-b border-[#cfcfcf] my-4"></div>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<div className="font-bold break-all">
										<div className="text-left md:max-w-[120px] break-all whitespace-normal">
											ゴール達成時 通知メッセージ
										</div>
									</div>
									<Tooltip
										title={<div>最大500文字です。</div>}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							name="ultimateGoalMess"
							rules={[
								{
									max: 500,
									message:
										"ゴール達成時 通知メッセージは500文字以内にしてください",
								},
							]}
						>
							<Input.TextArea
								allowClear
								style={{ resize: "none" }}
								className="min-w-[300px] max-w-[800px]  h-[145px]"
							/>
						</Form.Item>
						<div className="border-b border-[#cfcfcf] my-4"></div>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<span className="font-bold">中間ゴール</span>
									<Tooltip
										title={
											<div>
												ゴール達成前に、一定数のスタンプを貯めたユーザーに自動で通知メッセージを送ることができます。
											</div>
										}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							name="midtermGoal"
							className="w-[355px]"
						>
							<Select
								allowClear
								options={midtermGoalOptions}
								disabled={!maxMidtermGoal || maxMidtermGoal <= 1}
							/>
						</Form.Item>
						<Form.Item
							label={
								<p className="w-[220px] md:max-w-[220px] flex items-center">
									<div className="font-bold break-all">
										<div className="text-left  md:max-w-[120px] break-all whitespace-normal">
											中間ゴール達成時 通知メッセージ
										</div>
									</div>

									<Tooltip
										title={<div>最大500文字です。</div>}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							name="midtermGoalMess"
							rules={[
								{
									max: 500,
									message:
										"中間ゴール達成時 通知メッセージは500文字以内にしてください",
								},
							]}
						>
							<Input.TextArea
								allowClear
								style={{ resize: "none" }}
								className="min-w-[300px] max-w-[800px]  h-[145px]"
								disabled={!maxMidtermGoal || maxMidtermGoal <= 1}
							/>
						</Form.Item>
						<div className="border-b border-[#cfcfcf] my-4"></div>
						<Form.Item
							label={
								<p className="w-[220px] max-w-[220px] flex items-center">
									<div className="font-bold">
										<span className="text-left">注釈</span>
									</div>
									<Tooltip
										title={<div>最大255文字です。</div>}
										placement="bottom"
									>
										<QuestionCircleOutlined
											style={{
												marginLeft: 8,
												color: props?.publicSettings?.PRIMARY_COLOR,
											}}
										/>
									</Tooltip>
								</p>
							}
							name="note"
							rules={[
								{
									max: 255,
									message: "注釈は255文字以内にしてください",
								},
							]}
						>
							<Input.TextArea
								allowClear
								style={{ resize: "none" }}
								className="min-w-[300px] max-w-[800px] h-[145px]"
							/>
						</Form.Item>
						<div className="border-b border-[#cfcfcf] my-4"></div>
					</Form>
				</CustomFormItem>
			</div>
		</StyleScrollBarTextArea>
	);
};
export default CreateNewStamp;
